import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1613977257363-707ba9348227?w=1200&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/espace-client";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function dashboardFor(role) {
  if (["admin", "agent", "gestionnaire", "concierge"].includes(role)) return "/admin";
  if (role === "owner") return "/espace-proprietaire";
  if (role === "partenaire") return "/espace-partenaire";
  return "/espace-client";
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t("auth.welcomeToast"));
      navigate(location.state?.from || dashboardFor(user.role));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Logo variant="light" />
          <div>
            <h2 className="font-serif text-4xl font-bold text-white leading-tight">{t("auth.heroTitle1")}<br /><span className="text-gold">{t("auth.heroAccent")}</span></h2>
            <p className="mt-4 text-white/70 max-w-md">{t("auth.loginHeroSub")}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-ivory">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-serif text-3xl font-bold text-navy">{t("auth.loginTitle")}</h1>
          <p className="mt-2 text-slate-500">{t("auth.loginWelcome")}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div><Label>{t("auth.email")}</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="login-email" /></div>
            <div><Label>{t("auth.password")}</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="login-password" /></div>
            <Button type="submit" disabled={loading} className="w-full bg-navy text-white h-11 rounded-lg font-semibold" data-testid="login-submit">{loading ? t("auth.signingIn") : t("auth.signIn")}</Button>
          </form>
          <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" /> {t("auth.or")} <div className="h-px flex-1 bg-slate-200" /></div>
          <Button onClick={googleLogin} variant="outline" className="w-full h-11 rounded-lg" data-testid="google-login">
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            {t("auth.google")}
          </Button>
          <p className="mt-6 text-center text-sm text-slate-500">{t("auth.noAccount")} <Link to="/inscription" className="font-semibold text-gold hover:underline" data-testid="to-register">{t("auth.createAccount")}</Link></p>
        </div>
      </div>
    </div>
  );
}
